import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRegister } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { getProfiles, putProfile } from '@/api/adapter/onboarding';
import PasswordAuthForm from './components/PasswordAuthForm';
import SignupComplete from './components/SignupComplete';
import { Button } from '@/components/ui/Button';
import { DocumentModal } from '@/components/ui/DocumentModal';
import { TERMS_AND_CONDITIONS } from './constants/legalText';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export default function ClientRegisterPage() {
  const { clientRole } = useDaoConfig();
  const session = useAuthStore((state) => state.session);
  const status = useAuthStore((state) => state.status);
  const register = useRegister();
  const principalId = session?.principalId;
  const savedFor = useRef<string>();
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setComplete(false);
    setProfileLoaded(false);
    setError('');
    if (principalId)
      void getProfiles()
        .then((profiles) => {
          if (active && savedFor.current !== principalId) {
            useAuthStore.getState().setProfiles(profiles);
            setComplete(!!profiles.client);
            setProfileLoaded(true);
            const current = useAuthStore.getState();
            if (current.session?.principalId === principalId)
              current.setSession(current.session, 'client');
          }
        })
        .catch((reason: unknown) => {
          if (active)
            setError(
              reason instanceof Error ? reason.message : 'Cannot load profile.'
            );
        });
    return () => {
      active = false;
    };
  }, [principalId, loadAttempt]); // Same principal session checks must not reset an in-progress form.
  const save = async (name: string, email = contactEmail) => {
    const expectedPrincipal = useAuthStore.getState().session?.principalId;
    const profiles = await putProfile('client', {
      name,
      contactEmail: email || null,
      company: null,
      department: null,
      website: null,
      description: null,
      location: null,
      languages: [],
    });
    if (
      !expectedPrincipal ||
      profiles.principalId !== expectedPrincipal ||
      useAuthStore.getState().session?.principalId !== expectedPrincipal
    )
      throw new Error(
        'The signed-in account changed. Reload before continuing.'
      );
    useAuthStore.getState().setProfiles(profiles);
    savedFor.current = profiles.principalId;
    const current = useAuthStore.getState();
    if (current.session?.principalId === profiles.principalId)
      current.setSession(current.session, 'client');
    setComplete(true);
  };
  if (status === 'loading') return <p role="status">Checking session…</p>;
  if (complete && session) return <SignupComplete />;
  if (session && !profileLoaded)
    return (
      <div>
        {error ? (
          <>
            <p role="alert">{error}</p>
            <Button onClick={() => setLoadAttempt((value) => value + 1)}>
              Retry profile lookup
            </Button>
          </>
        ) : (
          <p role="status">Loading your {clientRole.toLowerCase()} profile…</p>
        )}
      </div>
    );
  return (
    <div className="onboardingContent">
      <h1>Create {clientRole} Account</h1>
      <p>
        Create a username and password first. You can add a passkey once your
        account is ready.
      </p>
      {session ? (
        <div>
          <p>
            Your account exists and you are signed in as {session.displayName}.
            Finish saving your {clientRole.toLowerCase()} profile.
          </p>
          <div className="inputGroup">
            <label htmlFor="contactEmail">Email (contact, optional)</label>
            <input
              id="contactEmail"
              type="email"
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value)}
            />
          </div>
          <Button
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError('');
              void save(session.displayName)
                .catch((reason: unknown) =>
                  setError(
                    reason instanceof Error
                      ? reason.message
                      : 'Profile save failed.'
                  )
                )
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Saving…' : `Finish ${clientRole.toLowerCase()} profile`}
          </Button>
        </div>
      ) : (
        <PasswordAuthForm
          mode="register"
          role="client"
          onOpenTerms={() => setTerms(true)}
          onSubmit={async (values) => {
            setError('');
            setContactEmail(values.contactEmail ?? '');
            try {
              await register.mutateAsync({
                ...values,
                name: values.name ?? '',
                role: 'client',
              });
              await save(values.name ?? '', values.contactEmail ?? '');
            } catch (reason) {
              setError(
                reason instanceof Error
                  ? reason.message
                  : 'Registration failed.'
              );
              throw reason;
            } finally {
              register.reset();
            }
          }}
        />
      )}
      {error && <p role="alert">{error}</p>}
      <p>Wallet signup is unavailable with this backend.</p>
      <p>
        <Link to="/login/client">Already have an account? Log in</Link>
      </p>
      <DocumentModal
        isOpen={terms}
        onClose={() => setTerms(false)}
        title="Términos y Condiciones"
        content={TERMS_AND_CONDITIONS}
      />
    </div>
  );
}
